import { ReactNode, useCallback, useState, useRef, useEffect } from "react";
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuPortal,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  ContextMenuSeparator,
} from "../ui/context-menu";
import {
  ChevronLeft,
  CircleUserRound,
  Plus,
  Tags,
  UserRoundPlus,
  TrashIcon,
} from "lucide-react";
import { ColorItem, ProjectLabel, ProjectUser } from "./types";
import { IconUser } from "../ui/icons";
import { Input } from "../ui/input";
import { COLOR_LIST } from "@/utils/constants";
import { useSupabase } from "../contexts/DatabaseSyncWrapper";
import { useDispatch } from "react-redux";
import { deleteCanvas } from "@/store/supabaseSlice";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { useSession } from "@clerk/clerk-react";
import { getUnexpiredToken } from "@/utils/utils";
import { useKanbanActions } from "@/hooks/useKanbanActions";
import { Project, Canvas } from "@/utils/types";
interface Props {
  children: ReactNode;
  project: Project;
  canvas: Canvas;
}

export function CanvasContextMenu({ children, project, canvas }: Props) {
  const [newLabelName, setNewLabelName] = useState<string>("");
  const [labelView, setLabelView] = useState<"labels" | "new" | "colorSelect">(
    "labels"
  );
  const [showDeleteCanvasModal, setShowDeleteCanvasModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const firstColorItemRef = useRef<HTMLDivElement>(null);

  const { supabaseClient } = useSupabase();
  const updateCanvasAssignedUserId =
    useKanbanActions().updateCanvasAssignedUserId;
  const updateCanvasLabels = useKanbanActions().updateCanvasLabels;
  const updateProjectLabels = useKanbanActions().updateProjectLabels;

  const dispatch = useDispatch<any>();

  const { session } = useSession();

  const renderUserIcon = useCallback(
    (user: ProjectUser) =>
      user.picture ? (
        <img
          src={user.picture}
          alt={user.name}
          className="select-none focus:outline-none rounded-full size-5"
          crossOrigin="anonymous"
        />
      ) : (
        <IconUser />
      ),
    []
  );

  const handleReset = useCallback(() => {
    setLabelView("labels");
    setNewLabelName("");
  }, [setNewLabelName, setLabelView]);

  useEffect(() => {
    if (labelView === "new" && inputRef.current) {
      inputRef.current?.focus();
    } else if (labelView === "colorSelect" && firstColorItemRef.current) {
      firstColorItemRef.current?.focus();
    }
  }, [labelView]);

  const handleUpdateLabel = useCallback(
    (label: ProjectLabel) => {
      const currentCanvasLabels = (canvas?.labels as string[]) || [];
      const newLabels = [...currentCanvasLabels];
      const index = newLabels.indexOf(label.id);
      if (index !== -1) {
        newLabels.splice(index, 1);
      } else {
        newLabels.push(label.id);
      }
      updateCanvasLabels(canvas, newLabels);
    },
    [canvas, updateCanvasLabels]
  );

  const handleOnColorSelect = useCallback(
    (color: ColorItem) => {
      const labelDataToCreate: Omit<ProjectLabel, "id"> = {
        label: newLabelName,
        color,
      };

      updateProjectLabels(
        project,
        (project.labels || []) as ProjectLabel[],
        labelDataToCreate
      )
        .then((createdLabel) => {
          if (createdLabel) {
            handleUpdateLabel(createdLabel);
            handleReset();
          }
        })
        .catch((error) => {
          console.error("Error during label creation promise chain:", error);
          toast.error("An unexpected error occurred while creating the label.");
        });
    },
    [newLabelName, project, updateProjectLabels, handleUpdateLabel, handleReset]
  );

  const handleUpdateCanvasAssignedId = useCallback(
    async (canvasItem: Canvas, assignedUserId: string | null) => {
      await updateCanvasAssignedUserId(canvasItem, assignedUserId);
    },
    [updateCanvasAssignedUserId]
  );

  const handleDeleteCanvas = async () => {
    if (!supabaseClient) {
      return;
    }

    try {
      await supabaseClient
        .from("canvases")
        .update({
          deleted_at: new Date().toISOString(),
        })
        .eq("id", canvas.id);

      dispatch(deleteCanvas(canvas.id));

      const supabaseJWT = await getUnexpiredToken(session);

      axios
        .delete(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${
            canvas.id
          }/cache`,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .catch((err) => {
          console.error("Error deleting canvas cache:", err.toString());
        });

      setShowDeleteCanvasModal(false);

      toast?.success("Canvas deleted successfully");
    } catch (error) {
      console.error("Error deleting canvas:", error);
      toast?.error(`Failed to delete canvas: ${error}`);
    }
  };

  const closeDeleteCanvasModal = () => {
    setShowDeleteCanvasModal(false);
  };

  return (
    <>
      <ContextMenu onOpenChange={handleReset}>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuPortal>
          <ContextMenuContent className="w-52">
            <ContextMenuSub>
              <ContextMenuSubTrigger className="flex gap-2 cursor-pointer">
                <UserRoundPlus className="size-4 text-secondary-n-400" />
                Assignee
              </ContextMenuSubTrigger>
              <ContextMenuPortal>
                <ContextMenuSubContent sideOffset={5} alignOffset={-5}>
                  <ContextMenuCheckboxItem
                    checked={!canvas.assigned_user_id}
                    className="flex gap-2 cursor-pointer"
                    onSelect={() => handleUpdateCanvasAssignedId(canvas, null)}
                  >
                    <CircleUserRound className="size-5" />
                    No assignee
                  </ContextMenuCheckboxItem>
                  {(project?.users as ProjectUser[])?.map(
                    (user: ProjectUser) => (
                      <ContextMenuCheckboxItem
                        key={user.id}
                        checked={canvas?.assigned_user_id === user.id}
                        className="flex gap-2 text-sm cursor-pointer"
                        onSelect={() =>
                          handleUpdateCanvasAssignedId(canvas, user.id)
                        }
                      >
                        {renderUserIcon(user)}
                        {user?.name ?? user?.email}
                      </ContextMenuCheckboxItem>
                    )
                  )}
                </ContextMenuSubContent>
              </ContextMenuPortal>
            </ContextMenuSub>
            <ContextMenuSub>
              <ContextMenuSubTrigger className="flex gap-2 cursor-pointer">
                <Tags className="size-4 text-secondary-n-400" />
                Labels
              </ContextMenuSubTrigger>
              <ContextMenuPortal>
                <ContextMenuSubContent
                  className="max-w-56"
                  sideOffset={5}
                  alignOffset={-5}
                >
                  {labelView === "labels" && (
                    <>
                      <ContextMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          setLabelView("new");
                        }}
                        className="flex justify-between items-center text-sm text-secondary-n-400 cursor-pointer"
                      >
                        New label
                        <Plus className="size-4" />
                      </ContextMenuItem>
                      {(project?.labels as ProjectLabel[])?.map(
                        (label: ProjectLabel) => (
                          <ContextMenuCheckboxItem
                            key={label.id}
                            checked={(canvas?.labels as string[])?.includes(
                              label.id
                            )}
                            onSelect={() => handleUpdateLabel(label)}
                            className="flex gap-2 items-center text-sm group cursor-pointer"
                            indicatorClassname="rounded group-hover:border border-secondary-n-300 hover:border-primary-n-600 [&>span]:bg-primary-n-600 [&>span]:rounded"
                          >
                            <div
                              className="size-2.5 rounded-full"
                              style={{
                                backgroundColor: label?.color?.value || "#000",
                              }}
                            />
                            <span className="w-36 overflow-hidden text-ellipsis">
                              {label?.label}
                            </span>
                          </ContextMenuCheckboxItem>
                        )
                      )}
                    </>
                  )}
                  {labelView === "new" && (
                    <>
                      <Input
                        ref={inputRef}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter" && newLabelName?.trim()) {
                            e.preventDefault();
                            setLabelView("colorSelect");
                          }
                        }}
                        placeholder="New label"
                        onKeyUp={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setNewLabelName(e.target.value);
                        }}
                      />
                      <ContextMenuItem
                        disabled={!newLabelName?.trim()}
                        className="flex justify-start gap-2 cursor-pointer"
                        onSelect={(e) => {
                          e.preventDefault();
                          setLabelView("colorSelect");
                        }}
                      >
                        <div>
                          <Plus className="size-4 inline-block break-words" />
                        </div>
                        <span className="overflow-hidden break-words">
                          Add new label: {newLabelName}
                        </span>
                      </ContextMenuItem>
                      <ContextMenuItem
                        className="flex justify-between gap-2 cursor-pointer"
                        onSelect={(e) => {
                          e.preventDefault();
                          setLabelView("labels");
                        }}
                      >
                        Back
                        <ChevronLeft className="size-4" />
                      </ContextMenuItem>
                    </>
                  )}
                  {labelView === "colorSelect" && (
                    <>
                      <ContextMenuItem
                        disabled
                        className="overflow-hidden break-words"
                      >
                        Pick a color for label: {newLabelName}
                      </ContextMenuItem>
                      {COLOR_LIST.map((color, index) => (
                        <ContextMenuCheckboxItem
                          key={color.key}
                          ref={index === 0 ? firstColorItemRef : null}
                          onSelect={() => {
                            handleOnColorSelect(color);
                          }}
                          className="flex gap-2 items-center text-sm cursor-pointer"
                        >
                          <div
                            className="size-2.5 rounded-full"
                            style={{ backgroundColor: color.value }}
                          />
                          <span>{color.label}</span>
                        </ContextMenuCheckboxItem>
                      ))}
                    </>
                  )}
                  <ContextMenuSeparator />
                </ContextMenuSubContent>
              </ContextMenuPortal>
              <ContextMenuSeparator />
              <ContextMenuItem
                className="flex justify-start gap-2 cursor-pointer text-destructive hover:text-destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  setShowDeleteCanvasModal(true);
                }}
              >
                <TrashIcon className="h-4 w-4" />
                <span>Delete Canvas</span>
              </ContextMenuItem>
            </ContextMenuSub>
          </ContextMenuContent>
        </ContextMenuPortal>
      </ContextMenu>

      <AlertDialog
        open={showDeleteCanvasModal}
        onOpenChange={(open: boolean) => {
          if (!open) {
            closeDeleteCanvasModal();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Canvas</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="w-full flex flex-col">
                <div className="pb-2">
                  Are you sure you want to delete this canvas? This cannot be
                  undone.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                closeDeleteCanvasModal();
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={handleDeleteCanvas}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
